import { parseColorChannels } from "./color.js";

function waitForProgram(
  gl: WebGL2RenderingContext,
  program: WebGLProgram,
  completion: number | null,
  signal?: AbortSignal,
): Promise<void> {
  return new Promise((resolve, reject) => {
    const channel = new MessageChannel();
    const close = (): void => {
      channel.port1.close();
      channel.port2.close();
      signal?.removeEventListener("abort", abort);
    };
    const abort = (): void => {
      close();
      reject(signal?.reason);
    };
    channel.port1.onmessage = () => {
      try {
        if (gl.isContextLost()) throw new Error("Shader setup: context lost");
        if (completion !== null && !gl.getProgramParameter(program, completion)) {
          channel.port2.postMessage(null);
          return;
        }
        close();
        resolve();
      } catch (error) {
        close();
        reject(error);
      }
    };
    signal?.addEventListener("abort", abort, { once: true });
    if (signal?.aborted) abort();
    else channel.port2.postMessage(null);
  });
}

export async function createShaderProgram(
  gl: WebGL2RenderingContext,
  vertexSource: string,
  fragmentSource: string,
  signal?: AbortSignal,
  /** Vertex outputs captured with transform feedback, interleaved in one buffer. */
  transformFeedbackVaryings: readonly string[] = [],
): Promise<WebGLProgram> {
  const shaders: WebGLShader[] = [];
  let program: WebGLProgram | null = null;
  let linked = false;
  const check = (): void => {
    signal?.throwIfAborted();
    if (gl.isContextLost()) throw new Error("Shader setup: context lost");
  };
  try {
    check();
    for (const [type, source] of [
      [gl.VERTEX_SHADER, vertexSource],
      [gl.FRAGMENT_SHADER, fragmentSource],
    ] as const) {
      const shader = gl.createShader(type);
      if (!shader) throw new Error("Shader setup: allocation failed");
      shaders.push(shader);
      gl.shaderSource(shader, source);
      gl.compileShader(shader);
    }
    program = gl.createProgram();
    if (!program) throw new Error("Shader program: allocation failed");
    for (const shader of shaders) gl.attachShader(program, shader);
    if (transformFeedbackVaryings.length)
      gl.transformFeedbackVaryings(program, [...transformFeedbackVaryings], gl.INTERLEAVED_ATTRIBS);
    gl.linkProgram(program);
    const extension: unknown = gl.getExtension("KHR_parallel_shader_compile");
    const completion =
      extension !== null &&
      typeof extension === "object" &&
      "COMPLETION_STATUS_KHR" in extension &&
      typeof extension.COMPLETION_STATUS_KHR === "number"
        ? extension.COMPLETION_STATUS_KHR
        : null;
    // Wait on tasks, not animation frames, so a static or hidden page still finishes setup.
    await waitForProgram(gl, program, completion, signal);
    check();
    if (!gl.getProgramParameter(program, gl.LINK_STATUS)) {
      const logs = [
        gl.getProgramInfoLog(program),
        ...shaders.map((shader) => gl.getShaderInfoLog(shader)),
      ];
      throw new Error(`Shader program link failed: ${logs.filter(Boolean).join("\n")}`);
    }
    linked = true;
    return program;
  } finally {
    // A linked program belongs to the caller. The shaders are temporary and die here.
    for (const shader of shaders) {
      if (program) gl.detachShader(program, shader);
      gl.deleteShader(shader);
    }
    if (program && !linked) gl.deleteProgram(program);
  }
}

/** Uniform locations by name. The constructor throws on a misspelled or optimized-out name,
    and `get` only accepts the declared names, so a typo doesn't even typecheck. */
export class UniformLocations<N extends string> {
  private readonly locations = new Map<N, WebGLUniformLocation>();

  constructor(
    gl: WebGL2RenderingContext,
    program: WebGLProgram,
    names: readonly N[],
    private readonly label: string,
  ) {
    for (const name of names) {
      const location = gl.getUniformLocation(program, name);
      if (!location)
        throw new Error(`${label}: uniform "${name}" not found (misspelled or optimized out)`);
      this.locations.set(name, location);
    }
  }

  get(name: N): WebGLUniformLocation {
    const location = this.locations.get(name);
    if (!location) throw new Error(`${this.label}: uniform "${name}" was not declared`);
    return location;
  }
}

export function requireAttribute(
  gl: WebGL2RenderingContext,
  program: WebGLProgram,
  name: string,
  label: string,
): number {
  const location = gl.getAttribLocation(program, name);
  if (location < 0)
    throw new Error(`${label}: attribute "${name}" not found (misspelled or optimized out)`);
  return location;
}

export function requireBuffer(gl: WebGL2RenderingContext, label: string): WebGLBuffer {
  const buffer = gl.createBuffer();
  if (!buffer) throw new Error(`${label}: buffer allocation failed`);
  return buffer;
}

/** A unit quad (a triangle strip with corners in [-1, 1]) on `location`, in the bound vertex
    array. The returned buffer belongs to the caller. */
export function createUnitQuad(
  gl: WebGL2RenderingContext,
  location: number,
  label: string,
): WebGLBuffer {
  const buffer = requireBuffer(gl, `${label} quad`);
  gl.bindBuffer(gl.ARRAY_BUFFER, buffer);
  gl.bufferData(gl.ARRAY_BUFFER, new Float32Array([-1, -1, 1, -1, -1, 1, 1, 1]), gl.STATIC_DRAW);
  gl.enableVertexAttribArray(location);
  gl.vertexAttribPointer(location, 2, gl.FLOAT, false, 0, 0);
  gl.vertexAttribDivisor(location, 0);
  return buffer;
}

/** Normalized RGB triples for a `vec3` palette uniform with `size` entries. A missing color
    stays black. */
export function paletteUniformData(
  colors: readonly (string | undefined)[],
  size: number,
): Float32Array {
  const data = new Float32Array(size * 3);
  for (let i = 0; i < colors.length && i < size; i++) {
    const color = colors[i];
    if (color === undefined) continue;
    const [r, g, b] = parseColorChannels(color);
    data[i * 3] = r / 255;
    data[i * 3 + 1] = g / 255;
    data[i * 3 + 2] = b / 255;
  }
  return data;
}
